import { chartEmptyValueConstants } from './chartEmptyValueConstants';

/**
 * Helper function to format month strings for display
 * Export this for use in chart transformers where formatted display is needed
 */
export const formatMonthString = (monthString) => {
  if (!/^\d{4}-\d{2}$/.test(monthString)) return monthString;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const [year, month] = monthString.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  return `${months[monthIndex]} ${year}`;
};

/**
 * Helper function to get a display name from an object or return the value directly
 * Checks common property names like name, title, label, or reason
 *
 * @param {Object|string} input - Input object or string value
 * @param {string} [fallbackKey] - Optional key to use for lookup
 * @returns {string} - Display name or original value
 */
const getDisplayName = (input, key) => {
  if (!input) return chartEmptyValueConstants[key];
  if (typeof input !== 'object') {
    // Keep original format (YYYY-MM) for proper sorting
    // Formatting should be done at display/render time, not at data transformation time
    return input;
  }
  return input?.name ?? input?.title ?? input?.reason ?? input?.label;
};

/**
 * Transforms a 2D dataet into a structured format with support for:
 * - Lookup value mapping (dt, g1, g2)
 * - Nested data structures (subRows)
 * - Custom header mapping
 * - Flexible data types
 *
 * @param {Object} config - Configuration object
 * @param {string[]} config.headers - Array of column headers
 * @param {Array<Array>} config.data - 2D array of data to transform
 * @param {Array} [config.g1=[]] - Lookup values for g1 column
 * @param {Array} [config.g2=[]] - Lookup values for g2 column
 * @param {Array} [config.dt=[]] - Lookup values for dt column
 * @param {string} [config.headerKey='dt'] - Key to use for header mapping
 * @param {string} [config.g1Key] - Key for g1 name lookup
 * @param {string} [config.g2Key] - Key for g2 name lookup
 * @returns {Array<Object>} Transformed data array
 *
 */
export const chartTableFlatTransformer = ({
  headers = [],
  data = [],
  g1 = [],
  g2 = [],
  dt = [],
  headerKey = 'dt',
  g1Key = undefined,
  g2Key = undefined,
  g1_l2 = [],
  g2_l2 = [],
}) => {
  // Input validation
  if (!Array.isArray(headers) || !Array.isArray(data)) {
    console.warn(
      'flatTransformer: Invalid input - headers and data must be arrays'
    );
    return [];
  }

  return data.map((row) => {
    // Transform each row into an object based on headers
    return headers.reduce((result, header, columnIndex) => {
      const value = row[columnIndex];

      // Handle different column types
      switch (header) {
        case 'dt':
          // Handle datetime lookup values
          if (typeof value === 'number' && dt[value]) {
            const rawDt = dt[value];
            result[header] = rawDt; // Keep raw YYYY-MM format for sorting
            result.dt_display = formatMonthString(rawDt); // Add formatted version for charts
            if (headerKey === 'dt') {
              result.header = getDisplayName(rawDt, g1Key);
            }
          } else {
            result[header] = value;
            result.dt_display = formatMonthString(value); // Also format direct string values
          }
          break;

        case 'g1':
          // Handle g1 group lookup values

          if (typeof value === 'number') {
            const g1Data = g1[value];
            result[header] = g1Data;
            if (Array.isArray(g1Data) && g1_l2?.length) {
              // Add mapped g1_l2 objects
              const g1Objects = g1Data.map((index) => g1_l2[index]);

              // Set g1 to be array of IDs from g1_l2
              result[header] = g1Objects.map((obj) => obj.id);

              // Add objects in g1_l2 property
              result['g1_l2'] = g1Objects;
              if (headerKey === 'g1') {
                result['header'] = g1Data
                  .map((index) => getDisplayName(g1_l2[index], g1Key))
                  .join(', ');
                result['parentHeader'] = g1Data.map((index) =>
                  getDisplayName(g1_l2[index], g1Key)
                );
              }
            } else {
              if (headerKey === 'g1') {
                result['header'] = getDisplayName(g1Data, g1Key);
                result['parentHeader'] = getDisplayName(g1Data, g1Key);
              }
              if (!g1Data) {
                result['parentHeader'] =
                  chartEmptyValueConstants[g1Key] || dt[value] || null;
              } else {
                result['parentHeader'] = getDisplayName(g1Data, g1Key);
              }
            }
          }
          // to do: null values for non existing g1
          //
          // else {
          //   result[header] = value;
          // }
          break;

        case 'g2':
          // Handle g2 group lookup values
          if (typeof value === 'number') {
            const g2Data = g2[value];
            result[header] = g2Data;
            if (Array.isArray(g2Data) && g2_l2?.length) {
              const g2Objects = g2Data.map((index) => g2_l2[index]);

              result[header] = g2Objects.map((obj) => obj.id);

              result['g1_l2'] = g2Objects;
              if (headerKey === 'g2') {
                result['header'] = g2Data
                  .map((index) => getDisplayName(g2_l2[index], g2Key))
                  .join(', ');
              }
            } else {
              if (headerKey === 'g2') {
                result['header'] = getDisplayName(g2Data, g2Key);
              }
              if (!g2Data) {
                result['emptyValue'] =
                  chartEmptyValueConstants[g2Key] || dt[value] || null;
              }
            }
          }
          // to do: null values for non existing g2
          // else {
          //   result[header] = value;
          // }
          break;

        case 'ds':
          // Handle nested data structures
          if (Array.isArray(value)) {
            result.ds = value;
            result.subRows = chartTableFlatTransformer({
              headers,
              data: value,
              g1,
              g2,
              dt,
              headerKey: 'g2',
              g1Key,
              g2Key,
              g1_l2,
              g2_l2,
            });
          } else {
            result[header] = value;
          }
          break;

        default:
          // Handle all other columns
          result[header] = value;
      }

      return result;
    }, {});
  });
};

/**
 * EXAMPLE SET 1: Basic Transformation
 * Demonstrates simple mapping of indices to lookup values

const headers1 = ['ds', 'dt', 'g1', 'g2'];
const data1 = [
  ['0', 0, 1, 0], // Row 1: id='0', dt=index 0, g1=index 1, g2=index 0
  ['1', 1, 0, 1], // Row 2: id='1', dt=index 1, g1=index 0, g2=index 1
];
const g1_1 = ['Group A', 'Group B']; // Lookup values for g1
const g2_1 = ['Category X', 'Category Y']; // Lookup values for g2
const dt_1 = ['2023-Q3', '2023-Q4']; // Lookup values for dt

// Result will be:
const result1 = [
  { ds: '0', dt: '2023-Q3', g1: 'Group B', g2: 'Category X' },
  { ds: '1', dt: '2023-Q4', g1: 'Group A', g2: 'Category Y' },
];

/**
 * EXAMPLE SET 2: Nested Data Structure
 * Demonstrates how the transformer handles hierarchical data

const headers2 = ['ds', 'dt', 'g1', 'g2'];
const data2 = [
  [
    // Parent row 1
    ['0a', 0, 0], // Child row 1
    ['0b', 0, 1], // Child row 2
  ],
  [
    // Parent row 2
    ['1a', 1, 0], // Child row 1
    ['1b', 1, 1], // Child row 2
  ],
];
const g1_2 = ['Team Alpha', 'Team Beta'];
const g2_2 = ['Type I', 'Type II'];
const dt_2 = ['2024-Q2', '2024-Q3'];

// Result will have parent rows with nested subRows:
const result2 = [
  {
    ds: [
      ['0a', 0, 0],
      ['0b', 0, 1],
    ],
    subRows: [
      { ds: '0a', dt: '2024-Q2', g1: 'Team Alpha', g2: 'Type I' },
      { ds: '0b', dt: '2024-Q2', g1: 'Team Alpha', g2: 'Type II' },
    ],
  },
  {
    ds: [
      ['1a', 1, 0],
      ['1b', 1, 1],
    ],
    subRows: [
      { ds: '1a', dt: '2024-Q3', g1: 'Team Beta', g2: 'Type I' },
      { ds: '1b', dt: '2024-Q3', g1: 'Team Beta', g2: 'Type II' },
    ],
  },
];

/**
 * EXAMPLE SET 3: Simplified Structure
 * Demonstrates usage with fewer columns

const headers3 = ['ds', 'dt', 'g2'];
const data3 = [
  ['A001', 0, 0], // Simple row with just ds, dt index, and g2 index
  ['A002', 1, 1],
];
const g2_3 = ['Product X', 'Product Y'];
const dt_3 = ['2025-Q1', '2025-Q2'];

// Results in simpler objects:
const result3 = [
  { ds: 'A001', dt: '2025-Q1', g2: 'Product X' },
  { ds: 'A002', dt: '2025-Q2', g2: 'Product Y' },
];

/**
 * EXAMPLE SET 4: Deeply Nested Structure
 * Shows how the transformer handles multiple levels of nesting

const headers4 = ['ds', 'dt'];
const data4 = [
  [['SubItem A'], [0]], // Nested array structure
  [['SubItem B'], [1]],
];
const dt_4 = ['2025-Q3', '2025-Q4'];

// Results in nested structure with subRows:
const result4 = [
  {
    ds: [['SubItem A']],
    subRows: [{ ds: 'SubItem A', dt: '2025-Q3' }],
  },
  {
    ds: [['SubItem B']],
    subRows: [{ ds: 'SubItem B', dt: '2024-Q4' }],
  },
];

/**
 * Key Transformation Rules:
 *
 * 1. Basic Mapping:
 *    - Numeric values in dt, g1, g2 columns are treated as indices
 *    - These indices are used to look up values in corresponding arrays
 *
 * 2. Nested Data:
 *    - When 'ds' column contains an array, it creates a subRows structure
 *    - The parent row keeps the original array in 'ds'
 *    - Child rows are transformed using the same rules
 *
 * 3. Header Mapping:
 *    - The headerKey parameter determines which column provides the header value
 *    - This is useful for creating hierarchical displays
 *
 * 4. Name Resolution:
 *    - Objects in lookup arrays can be complex with properties like name, title, etc.
 *    - The getDisplayName helper function extracts the appropriate display value
 */
